import { z } from "zod";
import { ApiError, request } from "./http";
import type { QueryIr, QueryResponse } from "./ir";
import {
  CreatedToken,
  CreateProjectResponse,
  EventsWire,
  Me,
  Org,
  PersonalToken,
  Project,
  PropertiesWire,
  PublicKey,
  QueryResponseSchema,
  Region,
  RegionToken,
  RotateKeyResponse,
  toEventDefinitions,
  toPropertyDefinitions,
  type CreateOrgInput,
  type CreateProjectInput,
  type CreateTokenInput,
  type EnvironmentName,
  type EventDefinition,
  type PropertyDefinition,
  type SignInInput,
  type SignUpInput,
} from "./schemas";

/**
 * Everything the console asks of the backend. Two implementations: `createHttpApi`
 * (the real control-plane + region APIs) and the fixture adapter behind
 * `VITE_USE_FIXTURES=1`. Screens only ever see this interface.
 */
export interface Api {
  // control plane — session cookie `atx_session`
  me(): Promise<Me | null>;
  signUp(input: SignUpInput): Promise<Me>;
  signIn(input: SignInInput): Promise<Me>;
  signOut(): Promise<void>;
  regions(): Promise<Region[]>;
  createOrg(input: CreateOrgInput): Promise<Org>;
  listProjects(orgId: string): Promise<Project[]>;
  createProject(orgId: string, input: CreateProjectInput): Promise<CreateProjectResponse>;
  getProject(projectId: string): Promise<Project>;
  listKeys(projectId: string): Promise<PublicKey[]>;
  rotateKey(projectId: string, keyId: string): Promise<RotateKeyResponse>;
  revokeKey(projectId: string, keyId: string): Promise<PublicKey>;
  listTokens(): Promise<PersonalToken[]>;
  createToken(input: CreateTokenInput): Promise<CreatedToken>;
  revokeToken(tokenId: string): Promise<void>;
  regionToken(projectId: string, environment: EnvironmentName): Promise<RegionToken>;
  // region data plane — bearer region token, called directly (never via the control plane)
  events(rt: RegionToken): Promise<EventDefinition[]>;
  /** Event properties (optionally of one event) plus person properties. */
  properties(rt: RegionToken, event?: string): Promise<PropertyDefinition[]>;
  query(rt: RegionToken, ir: QueryIr, signal?: AbortSignal): Promise<QueryResponse>;
}

const Empty = z.unknown();

export function createHttpApi(base = ""): Api {
  const cp = (path: string) => `${base}${path}`;
  const region = (rt: RegionToken, path: string) => `${rt.region_api_url.replace(/\/$/, "")}${path}`;
  const enc = encodeURIComponent;

  return {
    async me() {
      try {
        return await request(Me, cp("/app/me"));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    signUp: (input) => request(Me, cp("/app/auth/signup"), { method: "POST", body: input }),
    signIn: (input) => request(Me, cp("/app/auth/login"), { method: "POST", body: input }),
    signOut: async () => {
      await request(Empty, cp("/app/auth/logout"), { method: "POST" });
    },
    regions: () => request(z.object({ regions: z.array(Region) }), cp("/app/regions")).then((r) => r.regions),
    createOrg: (input) => request(Org, cp("/app/orgs"), { method: "POST", body: input }),
    listProjects: (orgId) =>
      request(z.object({ projects: z.array(Project) }), cp(`/app/orgs/${enc(orgId)}/projects`)).then((r) => r.projects),
    createProject: (orgId, input) =>
      request(CreateProjectResponse, cp(`/app/orgs/${enc(orgId)}/projects`), { method: "POST", body: input }),
    getProject: (projectId) => request(Project, cp(`/app/projects/${enc(projectId)}`)),
    listKeys: (projectId) =>
      request(z.object({ keys: z.array(PublicKey) }), cp(`/app/projects/${enc(projectId)}/keys`)).then((r) => r.keys),
    rotateKey: (projectId, keyId) =>
      request(RotateKeyResponse, cp(`/app/projects/${enc(projectId)}/keys/${enc(keyId)}/rotate`), {
        method: "POST",
        body: { keep_old: false },
      }),
    revokeKey: (projectId, keyId) =>
      request(PublicKey, cp(`/app/projects/${enc(projectId)}/keys/${enc(keyId)}/revoke`), { method: "POST" }),
    listTokens: () =>
      request(z.object({ tokens: z.array(PersonalToken) }), cp("/app/tokens")).then((r) => r.tokens),
    createToken: (input) =>
      request(CreatedToken, cp("/app/tokens"), {
        method: "POST",
        // The API takes "omit" for a token that never expires, not null.
        body: { ...input, expires_in_days: input.expires_in_days ?? undefined },
      }),
    revokeToken: async (tokenId) => {
      await request(Empty, cp(`/app/tokens/${enc(tokenId)}`), { method: "DELETE" });
    },
    regionToken: (projectId, environment) =>
      request(RegionToken, cp(`/app/projects/${enc(projectId)}/region-token`), {
        method: "POST",
        body: { environment },
      }),
    events: (rt) =>
      request(EventsWire, region(rt, "/schema/events?days=30"), { token: rt.token }).then(toEventDefinitions),
    properties: async (rt, event) => {
      const [ev, person] = await Promise.all([
        request(PropertiesWire, region(rt, `/schema/properties?type=event${event ? `&event=${enc(event)}` : ""}`), {
          token: rt.token,
        }),
        request(PropertiesWire, region(rt, "/schema/properties?type=person"), { token: rt.token }),
      ]);
      return [...toPropertyDefinitions(ev), ...toPropertyDefinitions(person)];
    },
    query: (rt, ir, signal) =>
      request(QueryResponseSchema, region(rt, "/query"), { method: "POST", body: { query: ir }, token: rt.token, signal }),
  };
}
